/**
 * useUpcomingBirthdays
 *
 * Nächste Geburtstage für das Vorstands-Dashboard. Nutzt die bestehende
 * Backend-Route `GET /api/members/birthdays?month=MM` (laufender + folgender
 * Monat) — bewusst kein neuer Endpunkt. Berechnung von Restzeit und Alter
 * passiert hier, damit die Karte im Dashboard nur noch rendert.
 *
 * Kein Auto-Polling: geladen wird beim Mount und über refresh().
 */
import { useCallback, useEffect, useState } from 'react';
import apiClient from '../utils/apiClient';

interface BirthdayRow {
  mg_nr: string | number;
  vorname: string | null;
  name: string | null;
  geb_am: string;
  foto_path?: string | null;
  foto_path_updated?: string | null;
}

export interface UpcomingBirthday {
  id: string;
  vorname: string;
  name: string;
  /** Nächster Geburtstag (heute oder in der Zukunft). */
  date: Date;
  /** Tage bis zum Geburtstag; 0 = heute. */
  daysUntil: number;
  /** Alter, das an diesem Geburtstag erreicht wird. */
  age: number;
  /** Runder Geburtstag (10, 20, …). */
  isJubilee: boolean;
  /** Cache-Buster für das Profilbild; null = kein Foto hinterlegt. */
  photoVersion: string | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/**
 * Nächstes Vorkommen eines Geburtstags ab `today`.
 * 29. Februar rutscht in Nicht-Schaltjahren auf den 1. März (Date-Überlauf).
 */
function nextOccurrence(birth: Date, today: Date): Date {
  let next = new Date(today.getFullYear(), birth.getMonth(), birth.getDate());
  if (next < today) {
    next = new Date(today.getFullYear() + 1, birth.getMonth(), birth.getDate());
  }
  return next;
}

export function toUpcoming(rows: BirthdayRow[], now = new Date(), limit = 4): UpcomingBirthday[] {
  const today = startOfDay(now);
  const seen = new Set<string>();
  const list: UpcomingBirthday[] = [];

  rows.forEach((row) => {
    const id = String(row.mg_nr);
    if (seen.has(id) || !row.geb_am) return;
    // Nur den Datumsteil lesen, sonst verschiebt die Zeitzone den Tag.
    const birth = new Date(`${String(row.geb_am).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(birth.getTime())) return;
    seen.add(id);

    const date = nextOccurrence(birth, today);
    const age = date.getFullYear() - birth.getFullYear();
    list.push({
      id,
      vorname: row.vorname || '',
      name: row.name || '',
      date,
      daysUntil: Math.round((date.getTime() - today.getTime()) / DAY_MS),
      age,
      isJubilee: age > 0 && age % 10 === 0,
      photoVersion: row.foto_path ? String(row.foto_path_updated || '1') : null,
    });
  });

  return list
    .sort((a, b) => a.daysUntil - b.daysUntil || a.vorname.localeCompare(b.vorname, 'de'))
    .slice(0, limit);
}

export function useUpcomingBirthdays(enabled: boolean, limit = 4) {
  const [birthdays, setBirthdays] = useState<UpcomingBirthday[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      const current = now.getMonth() + 1;
      const next = current === 12 ? 1 : current + 1;
      const [a, b] = await Promise.all(
        [current, next].map((m) =>
          apiClient.get<BirthdayRow[]>(`/api/members/birthdays?month=${m}`).then((r) => r.data || []),
        ),
      );
      setBirthdays(toUpcoming([...a, ...b], now, limit));
    } catch (e) {
      console.error('Vorstands-Dashboard: Geburtstage konnten nicht geladen werden', e);
      setError('Geburtstage konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [enabled, limit]);

  useEffect(() => {
    load();
  }, [load]);

  return { birthdays, loading, error, refresh: load };
}

export default useUpcomingBirthdays;
